import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material'

import { useUAInsightsMeta } from '../../hooks/useUAInsightsMeta'
import { useUAInsightsActions, useUAInsightsScope } from '../../hooks/useUAInsightsNavigation'
import { NetworkIcon } from '../NetworkIcon/NetworkIcon'

interface CreativeAdNetworkSelectorProps {
  size?: 'small' | 'medium'
  label?: string
  sx?: object
  /** When true, the underlying FormControl is greyed-out and unclickable. */
  disabled?: boolean
}

/**
 * Global ad-network filter for UA Insights. The selection is persisted to the
 * URL (`?network=<networkId>`) via the navigation context so every dialog and
 * hook in the feature reacts to the same value. Options and their order come
 * from `/meta`.
 */
export const CreativeAdNetworkSelector: FC<CreativeAdNetworkSelectorProps> = ({ size = 'small', label, sx, disabled = false }) => {
  const { t } = useTranslation('ua-insights')
  const { networkId } = useUAInsightsScope()
  const { setNetwork } = useUAInsightsActions()

  const { networks, networkById } = useUAInsightsMeta()
  const effectiveLabel = label ?? t('network_filter_label')

  const handleChange = (event: SelectChangeEvent<string>) => {
    const next = event.target.value
    setNetwork(next || null)
  }

  const selectValue = networkId && networkById(networkId) ? networkId : networks[0]?.id ?? ''

  if (networks.length === 0) return null

  return (
    <FormControl size={size} disabled={disabled} sx={{ minWidth: 200, ...sx }}>
      <Select
        value={selectValue}
        onChange={handleChange}
        color="primary"
        inputProps={{ 'aria-label': effectiveLabel }}
        renderValue={(value) => {
          const network = networkById(value)
          if (!network) return null
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <NetworkIcon network={network} />
              <span>{network.name}</span>
            </Box>
          )
        }}
        sx={{
          borderRadius: 999,
          backgroundColor: 'background.paper',
        }}
      >
        {networks.map((n) => (
          <MenuItem key={n.id} value={n.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <NetworkIcon network={n} />
              <span>{n.name}</span>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}
